import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { toast } from "sonner";
import { useAuth } from "@/app/AuthContext";
import { useLocalization } from "@voltie/localization-web";
import { localization, useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

import {
  fetchOrganizations,
  fetchOrganization,
  fetchDriverGroups,
  createDriverGroup,
  deleteDriverGroup,
  fetchDriverGroupMembers,
  addDriverGroupMember,
  removeDriverGroupMember,
  fetchChargerGroups,
  createChargerGroup,
  deleteChargerGroup,
  fetchChargerGroupMembers,
  addChargerGroupMember,
  removeChargerGroupMember,
  fetchAccessPolicies,
  createAccessPolicy,
  updateAccessPolicy,
  deleteAccessPolicy,
  fetchAccessRules,
  createAccessRule,
  updateAccessRule,
  deleteAccessRule,
  fetchRulePolicies,
  fetchUnits,
  Organization,
  DriverGroup,
  ChargerGroup,
  AccessPolicy,
  AccessRule,
} from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import {
  SearchableSelect,
} from "@/components/ui/searchable-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Search, X } from "@/components/icons";
import {
  autoDriverGroupName,
  autoGroupName,
  isAutoGroup,
  isReservedManualGroupName,
  RESERVED_GROUP_NAME_MESSAGE_KEY,
} from "./accessControlGroups";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/** Renders a rule/policy group reference, flagging rule-managed auto groups. */
function GroupRefCell({ name }: { name: string | undefined }) {
  const t = useT();
  if (!name) return <span className="opacity-40">?</span>;
  if (isAutoGroup(name)) {
    return (
      <span
        className="inline-flex items-center gap-1.5"
        title={t("operator.orgs.access.auto_group_hint")}
      >
        <span>{name}</span>
        <Badge variant="info" className="badge-sm">{t("operator.orgs.access.auto_badge")}</Badge>
      </span>
    );
  }
  return <span>{name}</span>;
}

/** Count how many policies reference a group */
function policyCountForGroup(policies: AccessPolicy[], groupId: number, field: "driver_group_id" | "charger_group_id") {
  return policies.filter((p) => p[field] === groupId).length;
}

// Known rule types; the label and help come from the catalog by the same code
// (operator.orgs.access.rule_type / rule_type_help). Other codes are shown raw.
const RULE_TYPE_VARIANT: Record<string, "success" | "error" | "info"> = {
  allow_only: "info",
  block: "error",
};

/* ------------------------------------------------------------------ */
/*  ComboInput — text input with autocomplete suggestions              */
/* ------------------------------------------------------------------ */

function UnitComboInput({ value, onChange, placeholder, orgUnitIds }: {
  value: string; onChange: (v: string) => void; placeholder?: string; orgUnitIds?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [debounced, setDebounced] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), 250);
    return () => clearTimeout(t);
  }, [value]);

  const unitsQ = useQuery({
    queryKey: ["units-search", debounced],
    queryFn: () => fetchUnits({ start: 0, end: 20, search: debounced || undefined }),
    enabled: open && debounced.length > 0,
  });
  const orgUnitSet = useMemo(() => orgUnitIds ? new Set(orgUnitIds) : null, [orgUnitIds]);
  const results = useMemo(() => {
    const units = unitsQ.data?.units ?? [];
    return orgUnitSet ? units.filter((u) => orgUnitSet.has(u.unit_id)) : units;
  }, [unitsQ.data, orgUnitSet]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative mt-2" ref={ref}>
      <Input
        className="font-mono"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-zinc-200 bg-white shadow-lg max-h-48 overflow-y-auto">
          {results.map((u) => (
            <button
              key={u.unit_id}
              type="button"
              className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-100 flex items-center gap-2"
              onClick={() => { onChange(u.unit_id); setOpen(false); }}
            >
              <span className="font-mono">{u.unit_id}</span>
              {u.name && <span className="text-xs opacity-50">{u.name}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Org selector                                                       */
/* ------------------------------------------------------------------ */

function OrgSelector({ orgs, selected, onSelect }: {
  orgs: Organization[]; selected: number | null; onSelect: (id: number) => void;
}) {
  const t = useT();
  if (orgs.length === 0) return <div className="text-sm opacity-50">{t("operator.orgs.selector_none")}</div>;
  return (
    <SearchableSelect
      className="w-64"
      options={orgs.map((o) => ({ value: String(o.id), label: o.name }))}
      value={selected != null ? String(selected) : null}
      onValueChange={(v) => onSelect(Number(v))}
      placeholder={t("operator.org_selector.placeholder")}
      searchPlaceholder={t("operator.org_selector.search_placeholder")}
      emptyText={t("operator.org_selector.no_match")}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Add Member Modals                                                  */
/* ------------------------------------------------------------------ */

function AddUserMemberModal({ open, onOpenChange, orgId, groupId, existingIds, onAdded }: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number; groupId: number;
  existingIds: Set<number>; onAdded: () => void;
}) {
  const t = useT();
  const [search, setSearch] = useState("");
  const orgQ = useQuery({ queryKey: ["organization", orgId], queryFn: () => fetchOrganization(orgId), enabled: open });
  const addM = useMutation({
    mutationFn: (userId: number) => addDriverGroupMember(orgId, groupId, userId),
    onSuccess: () => { toast.success(t("operator.orgs.user_added")); onAdded(); onOpenChange(false); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });
  const available = useMemo(
    () => (orgQ.data?.organization?.users ?? []).filter((u) => !existingIds.has(u.id) && (!search || u.username.toLowerCase().includes(search.toLowerCase()))),
    [orgQ.data, existingIds, search],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{t("operator.orgs.access.add_user_title")}</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input className="pl-9" placeholder={t("operator.orgs.access.search_org_users")} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {orgQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">{t("common.state.loading")}</div>}
          {available.length === 0 && !orgQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">{t("operator.orgs.no_matching_users")}</div>}
          {available.map((u) => (
            <div key={u.id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
              <span className="font-medium text-sm">{u.username}</span>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(u.id)} disabled={addM.isPending}>{t("common.action.add")}</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddUnitMemberModal({ open, onOpenChange, orgId, groupId, existingIds, onAdded, orgUnitIds }: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number; groupId: number;
  existingIds: Set<string>; onAdded: () => void; orgUnitIds?: string[];
}) {
  const t = useT();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);
  const unitsQ = useQuery({
    queryKey: ["units-search", debouncedSearch],
    queryFn: () => fetchUnits({ start: 0, end: 50, search: debouncedSearch || undefined }),
    enabled: open,
  });
  const addM = useMutation({
    mutationFn: (unitId: string) => addChargerGroupMember(orgId, groupId, unitId),
    onSuccess: () => { toast.success(t("operator.orgs.unit_added")); onAdded(); onOpenChange(false); setSearch(""); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });
  const orgUnitSet = useMemo(() => orgUnitIds ? new Set(orgUnitIds) : null, [orgUnitIds]);
  const available = useMemo(() => {
    let units = unitsQ.data?.units ?? [];
    if (orgUnitSet) units = units.filter((u) => orgUnitSet.has(u.unit_id));
    return units.filter((u) => !existingIds.has(u.unit_id));
  }, [unitsQ.data, existingIds, orgUnitSet]);
  const canAddCustom = search.trim() && !existingIds.has(search.trim()) && !available.some((u) => u.unit_id === search.trim());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{t("operator.orgs.access.add_unit_title")}</DialogTitle></DialogHeader>
        <form className="relative" onSubmit={(e) => { e.preventDefault(); if (search.trim() && !existingIds.has(search.trim())) addM.mutate(search.trim()); }}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50" />
          <Input className="pl-9 font-mono" placeholder={t("operator.orgs.search_units")} value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
        </form>
        <div className="max-h-64 overflow-y-auto space-y-1">
          {canAddCustom && (
            <div className="flex items-center justify-between py-2 px-2 rounded bg-blue-50">
              <span className="font-mono text-sm">{search.trim()}</span>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(search.trim())} disabled={addM.isPending}>{t("operator.orgs.access.add_typed_unit", { unitId: search.trim() })}</Button>
            </div>
          )}
          {unitsQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">{t("common.state.loading")}</div>}
          {available.length === 0 && !canAddCustom && !unitsQ.isLoading && <div className="text-sm opacity-50 py-4 text-center">{t("operator.orgs.access.no_matching_units_typed")}</div>}
          {available.slice(0, 50).map((u) => (
            <div key={u.unit_id} className="flex items-center justify-between py-2 px-2 rounded hover:bg-muted/50">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{u.unit_id}</span>
                {u.name && <span className="text-xs opacity-50">{u.name}</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => addM.mutate(u.unit_id)} disabled={addM.isPending}>{t("common.action.add")}</Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Rule Form Modal (create + edit)                                    */
/* ------------------------------------------------------------------ */

function RuleFormModal({
  open, onOpenChange, orgId, driverGroups, chargerGroups, orgUsers, onSaved,
  editRule, orgUnitIds,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number;
  driverGroups: DriverGroup[]; chargerGroups: ChargerGroup[];
  orgUsers: { id: number; username: string }[];
  onSaved: () => void;
  editRule?: AccessRule | null;
  orgUnitIds?: string[];
}) {
  const t = useT();
  const isEdit = !!editRule;
  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState<"allow_only" | "block" | "allow">("allow_only");
  const [driverTarget, setDriverTarget] = useState<"group" | "single">("group");
  const [driverGroupId, setDriverGroupId] = useState("");
  const [singleUserId, setSingleUserId] = useState("");
  const [chargerTarget, setChargerTarget] = useState<"group" | "single">("group");
  const [chargerGroupId, setChargerGroupId] = useState("");
  const [singleUnitId, setSingleUnitId] = useState("");

  // Load managed policies for edit dialog (read-only)
  const rulePoliciesQ = useQuery({
    queryKey: ["rule-policies", orgId, editRule?.id],
    queryFn: () => fetchRulePolicies(orgId, editRule!.id),
    enabled: isEdit && open,
  });

  useEffect(() => {
    if (!open) return;
    if (editRule) {
      setName(editRule.name);
      setRuleType(editRule.rule_type);
      // Detect if current groups are auto-managed
      const dg = driverGroups.find((g) => g.id === editRule.driver_group_id);
      const cg = chargerGroups.find((g) => g.id === editRule.charger_group_id);
      const driverIsAuto = dg && isAutoGroup(dg.name);
      const chargerIsAuto = cg && isAutoGroup(cg.name);
      setDriverTarget(driverIsAuto ? "single" : "group");
      setDriverGroupId(String(editRule.driver_group_id));
      setSingleUserId("");
      setChargerTarget(chargerIsAuto ? "single" : "group");
      setChargerGroupId(String(editRule.charger_group_id));
      setSingleUnitId("");
    } else {
      setName(""); setRuleType("allow_only");
      setDriverTarget("group"); setDriverGroupId(""); setSingleUserId("");
      setChargerTarget("group"); setChargerGroupId(""); setSingleUnitId("");
    }
  }, [open, editRule]);

  const submitM = useMutation({
    mutationFn: async () => {
      // Resolve driver group
      let targetDriverGroupId: number;
      if (driverTarget === "single") {
        // Reuse existing auto-group or create a new one; add the selected user
        const groupName = autoDriverGroupName(name.trim());
        const existing = driverGroups.find((g) => g.name === groupName);
        if (existing) {
          targetDriverGroupId = existing.id;
        } else if (isEdit) {
          // In edit mode, reuse the rule's current auto-group
          targetDriverGroupId = editRule!.driver_group_id;
        } else {
          const res = await createDriverGroup(orgId, groupName);
          targetDriverGroupId = res.driver_group.id;
        }
        if (singleUserId) {
          await addDriverGroupMember(orgId, targetDriverGroupId, Number(singleUserId)).catch(() => {});
        }
      } else {
        targetDriverGroupId = Number(driverGroupId);
      }

      // Resolve charger group
      let targetChargerGroupId: number;
      if (chargerTarget === "single") {
        // Reuse existing auto-group or create a new one; add the selected charger
        const groupName = autoGroupName(name.trim());
        const existing = chargerGroups.find((g) => g.name === groupName);
        if (existing) {
          targetChargerGroupId = existing.id;
        } else if (isEdit) {
          // In edit mode, reuse the rule's current auto-group
          targetChargerGroupId = editRule!.charger_group_id;
        } else {
          const res = await createChargerGroup(orgId, groupName);
          targetChargerGroupId = res.charger_group.id;
          await addChargerGroupMember(orgId, targetChargerGroupId, singleUnitId.trim());
        }
        if (singleUnitId.trim()) {
          await addChargerGroupMember(orgId, targetChargerGroupId, singleUnitId.trim()).catch(() => {});
        }
      } else {
        targetChargerGroupId = Number(chargerGroupId);
      }

      if (isEdit) {
        return updateAccessRule(orgId, editRule!.id, {
          name: name.trim(),
          driver_group_id: targetDriverGroupId,
          charger_group_id: targetChargerGroupId,
        });
      }
      return createAccessRule(orgId, {
        name: name.trim(),
        rule_type: ruleType,
        driver_group_id: targetDriverGroupId,
        charger_group_id: targetChargerGroupId,
      });
    },
    onSuccess: () => {
      toast.success(t(isEdit ? "operator.orgs.access.rule_updated" : "operator.orgs.access.rule_created"));
      onSaved();
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const driverValid = driverTarget === "group" ? !!driverGroupId : (isEdit || !!singleUserId);
  const chargerValid = chargerTarget === "group" ? !!chargerGroupId : (isEdit || !!singleUnitId.trim());
  const valid = name.trim() && driverValid && chargerValid;

  const managedPolicies = rulePoliciesQ.data?.policies ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t(isEdit ? "operator.orgs.access.rule_edit_title" : "operator.orgs.access.rule_new_title")}
            <Badge variant={RULE_TYPE_VARIANT[ruleType] ?? "neutral"} className="badge-sm uppercase">
              {t("operator.orgs.access.rule_type", { type: ruleType })}
            </Badge>
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitM.mutate(); }}>
          {/* Rule type selector — only for create */}
          {!isEdit && (
            <div>
              <Label>{t("operator.orgs.access.rule_type_question")}</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {(["allow_only", "block"] as const).map((rt) => {
                  return (
                    <button
                      key={rt}
                      type="button"
                      className={`rounded-lg border p-3 text-left text-xs transition-colors ${
                        ruleType === rt
                          ? "border-zinc-900 bg-zinc-50 ring-1 ring-zinc-900"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      onClick={() => setRuleType(rt)}
                    >
                      <div className="font-semibold">{t("operator.orgs.access.rule_type", { type: rt })}</div>
                      <div className="mt-1 opacity-60">{t("operator.orgs.access.rule_type_help", { type: rt })}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <Label>{t("operator.orgs.access.rule_name_label")}</Label>
            <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)}
              placeholder={t("operator.orgs.access.rule_name_placeholder", { type: ruleType === "allow_only" ? "allow_only" : "block" })} />
          </div>

          {/* Who — driver group or single user */}
          <div>
            <Label>{t("operator.orgs.access.who_label", { type: ruleType === "allow_only" ? "allow_only" : "block" })}</Label>
            <div className="flex gap-2 mt-1.5">
              <Button type="button" size="sm" variant={driverTarget === "group" ? "default" : "outline"} onClick={() => setDriverTarget("group")}>{t("operator.orgs.access.target_user_group")}</Button>
              <Button type="button" size="sm" variant={driverTarget === "single" ? "default" : "outline"} onClick={() => setDriverTarget("single")}>{t(isEdit ? "operator.orgs.access.target_add_user" : "operator.orgs.access.target_single_user")}</Button>
            </div>
            {driverTarget === "group" ? (
              <SearchableSelect
                className="mt-2 w-full"
                value={driverGroupId || null}
                onValueChange={setDriverGroupId}
                placeholder={t("operator.orgs.access.driver_group_select")}
                searchPlaceholder={t("operator.orgs.access.driver_group_search")}
                emptyText={t("operator.orgs.access.driver_group_no_match")}
                options={driverGroups
                  .filter((g) => !(ruleType === "allow_only" && g.is_builtin) && !isAutoGroup(g.name))
                  .map((g) => ({
                    value: String(g.id),
                    label: t("operator.orgs.access.driver_group_option", { name: g.name, builtin: g.is_builtin ? "yes" : "no", count: g.member_count }),
                  }))}
              />
            ) : (
              <SearchableSelect
                className="mt-2 w-full"
                value={singleUserId || null}
                onValueChange={setSingleUserId}
                placeholder={t(isEdit ? "operator.orgs.access.user_add_to_rule" : "operator.orgs.access.user_select")}
                searchPlaceholder={t("operator.orgs.search_users")}
                emptyText={t("operator.orgs.access.user_no_match")}
                options={orgUsers.map((u) => ({ value: String(u.id), label: u.username }))}
              />
            )}
          </div>

          {/* Where — charger group or single charger */}
          <div>
            <Label>{t("operator.orgs.access.where_label")}</Label>
            <div className="flex gap-2 mt-1.5">
              <Button type="button" size="sm" variant={chargerTarget === "group" ? "default" : "outline"} onClick={() => setChargerTarget("group")}>{t("operator.orgs.access.target_charger_group")}</Button>
              <Button type="button" size="sm" variant={chargerTarget === "single" ? "default" : "outline"} onClick={() => setChargerTarget("single")}>{t(isEdit ? "operator.orgs.access.target_add_charger" : "operator.orgs.access.target_single_charger")}</Button>
            </div>
            {chargerTarget === "group" ? (
              <SearchableSelect
                className="mt-2 w-full"
                value={chargerGroupId || null}
                onValueChange={setChargerGroupId}
                placeholder={t("operator.orgs.access.charger_group_select")}
                searchPlaceholder={t("operator.orgs.access.charger_group_search")}
                emptyText={t("operator.orgs.access.charger_group_no_match")}
                options={chargerGroups
                  .filter((g) => !isAutoGroup(g.name))
                  .map((g) => ({ value: String(g.id), label: t("operator.orgs.access.charger_group_option", { name: g.name, count: g.member_count }) }))}
              />
            ) : (
              <UnitComboInput value={singleUnitId} onChange={setSingleUnitId}
                placeholder={t("operator.orgs.access.charger_id_placeholder")} orgUnitIds={orgUnitIds} />
            )}
          </div>

          {/* Read-only managed policies (edit only) */}
          {isEdit && managedPolicies.length > 0 && (
            <details className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
              <summary className="cursor-pointer text-xs font-medium text-zinc-500">
                {t("operator.orgs.access.managed_policies", { count: managedPolicies.length })}
              </summary>
              <div className="mt-2 space-y-1">
                {managedPolicies.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 text-xs text-zinc-600">
                    <Badge variant={p.rule_type === "deny" ? "error" : "success"} className="badge-sm uppercase">{t("operator.orgs.access.policy_type", { type: p.rule_type })}</Badge>
                    <span className="font-mono">{t("operator.orgs.access.priority_value", { priority: String(p.priority) })}</span>
                    <span className="truncate">{p.name}</span>
                  </div>
                ))}
              </div>
            </details>
          )}

          <DialogFooter>
            <Button type="submit" disabled={submitM.isPending || !valid}>
              {submitM.isPending ? t("common.state.saving") : t(isEdit ? "operator.orgs.access.rule_update" : "operator.orgs.access.rule_create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Legacy Policy Form Modal (for advanced/manual policies)            */
/* ------------------------------------------------------------------ */

function PolicyFormModal({
  open, onOpenChange, orgId, driverGroups, chargerGroups, onSaved,
  editPolicy, orgUnitIds,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; orgId: number;
  driverGroups: DriverGroup[]; chargerGroups: ChargerGroup[];
  onSaved: () => void;
  editPolicy?: AccessPolicy | null;
  orgUnitIds?: string[];
}) {
  const t = useT();
  const isEdit = !!editPolicy;
  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState<"grant" | "deny">("grant");
  const [driverGroupId, setDriverGroupId] = useState("");
  const [chargerTarget, setChargerTarget] = useState<"group" | "single">("group");
  const [chargerGroupId, setChargerGroupId] = useState("");
  const [singleUnitId, setSingleUnitId] = useState("");
  const [priority, setPriority] = useState("100");

  useEffect(() => {
    if (!open) return;
    if (editPolicy) {
      setName(editPolicy.name);
      setRuleType(editPolicy.rule_type);
      setDriverGroupId(String(editPolicy.driver_group_id));
      setChargerGroupId(String(editPolicy.charger_group_id));
      setChargerTarget("group");
      setSingleUnitId("");
      setPriority(String(editPolicy.priority));
    } else {
      setName(""); setRuleType("grant"); setDriverGroupId("");
      setChargerTarget("group"); setChargerGroupId(""); setSingleUnitId("");
      setPriority("100");
    }
  }, [open, editPolicy]);

  const submitM = useMutation({
    mutationFn: async () => {
      let targetGroupId: number;

      if (chargerTarget === "single" && !isEdit) {
        const groupName = autoGroupName(name.trim());
        const existing = chargerGroups.find((g) => g.name === groupName);
        if (existing) {
          targetGroupId = existing.id;
          await addChargerGroupMember(orgId, targetGroupId, singleUnitId).catch(() => {});
        } else {
          const res = await createChargerGroup(orgId, groupName);
          targetGroupId = res.charger_group.id;
          await addChargerGroupMember(orgId, targetGroupId, singleUnitId);
        }
      } else {
        targetGroupId = Number(chargerGroupId);
      }

      if (isEdit) {
        return updateAccessPolicy(orgId, editPolicy!.id, {
          name: name.trim(),
          rule_type: ruleType,
          driver_group_id: Number(driverGroupId),
          charger_group_id: targetGroupId,
          priority: Number(priority),
        });
      }
      return createAccessPolicy(orgId, {
        name: name.trim(),
        rule_type: ruleType,
        driver_group_id: Number(driverGroupId),
        charger_group_id: targetGroupId,
        priority: Number(priority),
      });
    },
    onSuccess: () => {
      toast.success(t(isEdit ? "operator.orgs.access.policy_updated" : "operator.orgs.access.policy_created"));
      onSaved();
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const valid = name.trim() && driverGroupId && (chargerTarget === "group" ? !!chargerGroupId : !!singleUnitId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t(isEdit ? "operator.orgs.access.policy_edit_title" : "operator.orgs.access.policy_new_title")}
            {ruleType === "deny"
              ? <Badge variant="error" className="badge-sm uppercase">{t("operator.orgs.access.policy_type", { type: "deny" })}</Badge>
              : <Badge variant="success" className="badge-sm uppercase">{t("operator.orgs.access.policy_type", { type: "grant" })}</Badge>}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitM.mutate(); }}>
          <div>
            <Label>{t("operator.orgs.access.policy_name_label")}</Label>
            <Input className="mt-1.5" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("operator.orgs.access.policy_name_placeholder")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("operator.orgs.access.policy_rule_type_label")}</Label>
              <div className="flex gap-2 mt-1.5">
                <Badge
                  className={`cursor-pointer px-3 py-1.5 text-xs ${ruleType === "grant" ? "" : "opacity-30"}`}
                  variant="success"
                  onClick={() => setRuleType("grant")}
                >{t("operator.orgs.access.policy_type_choice", { type: "grant" })}</Badge>
                <Badge
                  className={`cursor-pointer px-3 py-1.5 text-xs ${ruleType === "deny" ? "" : "opacity-30"}`}
                  variant="error"
                  onClick={() => setRuleType("deny")}
                >{t("operator.orgs.access.policy_type_choice", { type: "deny" })}</Badge>
              </div>
            </div>
            <div>
              <Label>{t("operator.orgs.access.priority_label")}</Label>
              <Input className="mt-1.5" type="number" value={priority} onChange={(e) => setPriority(e.target.value)} min={1} max={999} />
              <div className="text-xs opacity-50 mt-1">{t("operator.orgs.access.priority_help")}</div>
            </div>
          </div>
          <div>
            <Label>{t("operator.orgs.access.policy_who_label")}</Label>
            <SearchableSelect
              className="mt-1.5 w-full"
              value={driverGroupId || null}
              onValueChange={setDriverGroupId}
              placeholder={t("operator.orgs.access.driver_group_select")}
              searchPlaceholder={t("operator.orgs.access.driver_group_search")}
              emptyText={t("operator.orgs.access.driver_group_no_match")}
              options={driverGroups.map((g) => ({
                value: String(g.id),
                label: t("operator.orgs.access.driver_group_option", { name: g.name, builtin: g.is_builtin ? "yes" : "no", count: g.member_count }),
              }))}
            />
          </div>
          <div>
            <Label>{t("operator.orgs.access.where_label")}</Label>
            {!isEdit && (
              <div className="flex gap-2 mt-1.5">
                <Button type="button" size="sm" variant={chargerTarget === "group" ? "default" : "outline"} onClick={() => setChargerTarget("group")}>{t("operator.orgs.access.target_charger_group")}</Button>
                <Button type="button" size="sm" variant={chargerTarget === "single" ? "default" : "outline"} onClick={() => setChargerTarget("single")}>{t("operator.orgs.access.target_single_charger")}</Button>
              </div>
            )}
            {chargerTarget === "group" || isEdit ? (
              <SearchableSelect
                className="mt-2 w-full"
                value={chargerGroupId || null}
                onValueChange={setChargerGroupId}
                placeholder={t("operator.orgs.access.charger_group_select")}
                searchPlaceholder={t("operator.orgs.access.charger_group_search")}
                emptyText={t("operator.orgs.access.charger_group_no_match")}
                options={chargerGroups.map((g) => ({ value: String(g.id), label: t("operator.orgs.access.charger_group_option", { name: g.name, count: g.member_count }) }))}
              />
            ) : (
              <UnitComboInput value={singleUnitId} onChange={setSingleUnitId}
                placeholder={t("operator.orgs.access.charger_id_placeholder")} orgUnitIds={orgUnitIds} />
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitM.isPending || !valid}>
              {submitM.isPending ? t("common.state.saving") : t(isEdit ? "operator.orgs.access.policy_update" : "operator.orgs.access.policy_create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Delete group confirmation dialog                                   */
/* ------------------------------------------------------------------ */

function DeleteGroupDialog({ open, onOpenChange, groupName, policyCount, onConfirm, isPending }: {
  open: boolean; onOpenChange: (v: boolean) => void; groupName: string;
  policyCount: number; onConfirm: () => void; isPending: boolean;
}) {
  const t = useT();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("operator.orgs.access.delete_group_title")}</DialogTitle>
          <DialogDescription>
            {t("operator.orgs.access.delete_group_body", { name: groupName, count: policyCount })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.action.cancel")}</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending ? t("operator.orgs.access.deleting") : t("operator.orgs.access.delete_group_confirm", { count: policyCount })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Group Card                                                         */
/* ------------------------------------------------------------------ */

function GroupCard({ group, type, orgId, policies, onDeleted, userMap, orgUnitIds }: {
  group: DriverGroup | ChargerGroup; type: "driver" | "charger"; orgId: number;
  policies: AccessPolicy[]; onDeleted: () => void; userMap?: Map<number, string>; orgUnitIds?: string[];
}) {
  const t = useT();
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isBuiltin = "is_builtin" in group && group.is_builtin;
  const field = type === "driver" ? "driver_group_id" : "charger_group_id";
  const refCount = policyCountForGroup(policies, group.id, field);

  const driverMembersQ = useQuery({ queryKey: ["driver-group-members", orgId, group.id], queryFn: () => fetchDriverGroupMembers(orgId, group.id), enabled: type === "driver" });
  const chargerMembersQ = useQuery({ queryKey: ["charger-group-members", orgId, group.id], queryFn: () => fetchChargerGroupMembers(orgId, group.id), enabled: type === "charger" });
  const membersQ = type === "driver" ? driverMembersQ : chargerMembersQ;

  const deleteM = useMutation({
    mutationFn: () => type === "driver" ? deleteDriverGroup(orgId, group.id) : deleteChargerGroup(orgId, group.id),
    onSuccess: () => { toast.success(t("operator.orgs.access.group_deleted")); setDeleteOpen(false); onDeleted(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const removeMemberM = useMutation({
    mutationFn: (memberId: string | number) =>
      type === "driver" ? removeDriverGroupMember(orgId, group.id, memberId as number) : removeChargerGroupMember(orgId, group.id, memberId as string),
    onSuccess: () => { toast.success(t("operator.orgs.access.member_removed")); membersQ.refetch(); qc.invalidateQueries({ queryKey: [`${type}-groups`, orgId] }); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const members: (string | number)[] = type === "driver" ? (driverMembersQ.data?.members ?? []) : (chargerMembersQ.data?.members ?? []);
  const refreshMembers = () => { membersQ.refetch(); qc.invalidateQueries({ queryKey: [`${type}-groups`, orgId] }); };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">{group.name}</CardTitle>
            {isBuiltin && <Badge variant="secondary" className="badge-sm">{t("operator.orgs.access.builtin_badge")}</Badge>}
            {isAutoGroup(group.name) && <Badge variant="info" className="badge-sm">{t("operator.orgs.access.auto_badge")}</Badge>}
            {refCount > 0 && <Badge variant="neutral" className="badge-sm">{t("operator.orgs.access.rule_count", { count: refCount })}</Badge>}
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>{t("operator.orgs.access.add_member")}</Button>
            {!isBuiltin && (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive"
                aria-label={t("operator.orgs.access.delete_group_title")}
                onClick={() => setDeleteOpen(true)}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {members.length === 0 ? (
          <div className="text-sm opacity-50 italic">{t("operator.orgs.access.no_members")}</div>
        ) : (
          <div className="space-y-1">
            {members.map((m) => (
              <div key={String(m)} className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted/50 text-sm">
                <span className={type === "charger" ? "font-mono" : "font-medium"}>
                  {type === "driver" && userMap ? (userMap.get(m as number) ?? String(m)) : String(m)}
                </span>
                {!isBuiltin && (
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-destructive hover:text-destructive"
                    aria-label={t("common.action.remove")}
                    onClick={() => removeMemberM.mutate(m)}>
                    <X className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {type === "driver" && addOpen && (
        <AddUserMemberModal open={addOpen} onOpenChange={setAddOpen} orgId={orgId} groupId={group.id}
          existingIds={new Set(members as number[])} onAdded={refreshMembers} />
      )}
      {type === "charger" && addOpen && (
        <AddUnitMemberModal open={addOpen} onOpenChange={setAddOpen} orgId={orgId} groupId={group.id}
          existingIds={new Set(members as string[])} onAdded={refreshMembers} orgUnitIds={orgUnitIds} />
      )}
      <DeleteGroupDialog open={deleteOpen} onOpenChange={setDeleteOpen} groupName={group.name}
        policyCount={refCount} onConfirm={() => deleteM.mutate()} isPending={deleteM.isPending} />
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

export function AccessControlPage() {
  // language/revision rebuild the memoized table columns after a catalog switch.
  const { t, language, revision } = useLocalization(localization);
  const qc = useQueryClient();
  const { user, isAdmin } = useAuth();
  const [selectedOrg, setSelectedOrg] = useState<number | null>(null);
  const [newDriverName, setNewDriverName] = useState("");
  const [newChargerName, setNewChargerName] = useState("");
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AccessRule | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<AccessPolicy | null>(null);

  const orgsQ = useQuery({ queryKey: ["organizations"], queryFn: fetchOrganizations });
  const orgs = orgsQ.data?.organizations ?? [];
  // Show the org switcher to anyone who has a choice to make: a system admin
  // (sees all orgs) OR a manager of more than one org. /api/organizations already
  // scopes the list to the orgs the caller may manage, so no extra filtering here.
  const showOrgSelector = isAdmin || orgs.length > 1;

  // Auto-select org: for non-admin pick their only org; for admin pick first available
  useEffect(() => {
    if (selectedOrg) return;
    if (orgs.length > 0) setSelectedOrg(orgs[0].id);
  }, [orgsQ.data, selectedOrg]);

  const orgDetailQ = useQuery({
    queryKey: ["organization", selectedOrg],
    queryFn: () => fetchOrganization(selectedOrg!),
    enabled: !!selectedOrg,
  });

  const userMap = useMemo(() => {
    const users = orgDetailQ.data?.organization?.users ?? [];
    return new Map(users.map((u) => [u.id, u.username]));
  }, [orgDetailQ.data]);

  const driverGroupsQ = useQuery({ queryKey: ["driver-groups", selectedOrg], queryFn: () => fetchDriverGroups(selectedOrg!), enabled: !!selectedOrg });
  const chargerGroupsQ = useQuery({ queryKey: ["charger-groups", selectedOrg], queryFn: () => fetchChargerGroups(selectedOrg!), enabled: !!selectedOrg });
  const rulesQ = useQuery({ queryKey: ["access-rules", selectedOrg], queryFn: () => fetchAccessRules(selectedOrg!), enabled: !!selectedOrg });
  // Fetch all policies (including managed) for group reference counting
  const allPoliciesQ = useQuery({ queryKey: ["access-policies-all", selectedOrg], queryFn: () => fetchAccessPolicies(selectedOrg!, true), enabled: !!selectedOrg });
  // Fetch only manual (non-managed) policies for the advanced section
  const manualPoliciesQ = useQuery({ queryKey: ["access-policies-manual", selectedOrg], queryFn: () => fetchAccessPolicies(selectedOrg!), enabled: !!selectedOrg });

  const createDriverM = useMutation({
    mutationFn: () => createDriverGroup(selectedOrg!, newDriverName.trim()),
    onSuccess: () => { toast.success(t("operator.orgs.access.driver_group_created")); setNewDriverName(""); driverGroupsQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const createChargerM = useMutation({
    mutationFn: () => createChargerGroup(selectedOrg!, newChargerName.trim()),
    onSuccess: () => { toast.success(t("operator.orgs.access.charger_group_created")); setNewChargerName(""); chargerGroupsQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const toggleRuleM = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) => updateAccessRule(selectedOrg!, id, { enabled }),
    onSuccess: () => { rulesQ.refetch(); allPoliciesQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const deleteRuleM = useMutation({
    mutationFn: (ruleId: number) => deleteAccessRule(selectedOrg!, ruleId),
    onSuccess: () => { toast.success(t("operator.orgs.access.rule_deleted")); rulesQ.refetch(); allPoliciesQ.refetch(); chargerGroupsQ.refetch(); driverGroupsQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const togglePolicyM = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) => updateAccessPolicy(selectedOrg!, id, { enabled }),
    onSuccess: () => { manualPoliciesQ.refetch(); allPoliciesQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const deletePolicyM = useMutation({
    mutationFn: async (policy: AccessPolicy) => {
      await deleteAccessPolicy(selectedOrg!, policy.id);
      const cg = chargerGroups.find((g) => g.id === policy.charger_group_id);
      if (cg && isAutoGroup(cg.name)) {
        const allPols = allPolicies;
        const otherRefs = allPols.filter((p) => p.id !== policy.id && p.charger_group_id === cg.id).length;
        if (otherRefs === 0) {
          await deleteChargerGroup(selectedOrg!, cg.id).catch(() => {});
        }
      }
    },
    onSuccess: () => { toast.success(t("operator.orgs.access.policy_deleted")); manualPoliciesQ.refetch(); allPoliciesQ.refetch(); chargerGroupsQ.refetch(); },
    onError: (e) => toast.error(errorMessage(t, e, { fallback: "operator.orgs.access.failed" })),
  });

  const allDriverGroups = driverGroupsQ.data?.driver_groups ?? [];
  const allChargerGroups = chargerGroupsQ.data?.charger_groups ?? [];
  const driverGroups = allDriverGroups;
  const chargerGroups = allChargerGroups;
  const visibleDriverGroups = allDriverGroups.filter((g) => !isAutoGroup(g.name) && !g.is_builtin);
  const visibleChargerGroups = allChargerGroups.filter((g) => !isAutoGroup(g.name));
  const rules = rulesQ.data?.access_rules ?? [];
  const allPolicies = allPoliciesQ.data?.access_policies ?? [];
  const manualPolicies = manualPoliciesQ.data?.access_policies ?? [];

  const driverGroupMap = useMemo(() => new Map(driverGroups.map((g) => [g.id, g.name])), [driverGroups]);
  const chargerGroupMap = useMemo(() => new Map(chargerGroups.map((g) => [g.id, g.name])), [chargerGroups]);

  const refreshAll = () => { rulesQ.refetch(); allPoliciesQ.refetch(); manualPoliciesQ.refetch(); chargerGroupsQ.refetch(); driverGroupsQ.refetch(); };

  // Rules table columns
  const ruleColumns = useMemo<ColumnDef<AccessRule, any>[]>(
    () => [
      {
        accessorKey: "rule_type",
        header: t("operator.orgs.access.column_type"),
        cell: ({ getValue }) => {
          const v = getValue() as string;
          return <Badge variant={RULE_TYPE_VARIANT[v] ?? "neutral"} className="badge-sm">{t("operator.orgs.access.rule_type", { type: v })}</Badge>;
        },
      },
      {
        accessorKey: "name",
        header: t("operator.orgs.access.column_rule_name"),
        cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
      },
      {
        accessorKey: "driver_group_id",
        header: t("operator.orgs.access.column_who"),
        cell: ({ getValue }) => <GroupRefCell name={driverGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "charger_group_id",
        header: t("operator.orgs.access.column_where"),
        cell: ({ getValue }) => <GroupRefCell name={chargerGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "enabled",
        header: t("operator.orgs.access.column_status"),
        cell: ({ row }) => (
          <Button size="xs"
            variant={row.original.enabled ? "default" : "outline"}
            onClick={(e) => { e.stopPropagation(); toggleRuleM.mutate({ id: row.original.id, enabled: !row.original.enabled }); }}
          >
            {t("operator.orgs.access.enabled_toggle", { state: row.original.enabled ? "on" : "off" })}
          </Button>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button size="xs" variant="outline"
              onClick={(e) => { e.stopPropagation(); setEditingRule(row.original); }}>
              {t("common.action.edit")}
            </Button>
            <Button size="icon-xs" variant="ghost" className="text-destructive hover:text-destructive"
              aria-label={t("common.action.delete")}
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(t("operator.orgs.access.rule_delete_confirm", { name: row.original.name }))) {
                  deleteRuleM.mutate(row.original.id);
                }
              }}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
    ],
    [driverGroupMap, chargerGroupMap, t, language, revision],
  );

  // Manual policies table columns
  const policyColumns = useMemo<ColumnDef<AccessPolicy, any>[]>(
    () => [
      {
        accessorKey: "priority",
        header: t("operator.orgs.access.priority_label"),
        cell: ({ getValue }) => <span className="font-mono text-xs">{getValue()}</span>,
      },
      {
        accessorKey: "rule_type",
        header: t("operator.orgs.access.column_type"),
        cell: ({ getValue }) => {
          const v = getValue() as string;
          return <Badge variant={v === "deny" ? "error" : "success"} className="badge-sm uppercase">{t("operator.orgs.access.policy_type", { type: v })}</Badge>;
        },
      },
      {
        accessorKey: "name",
        header: t("operator.orgs.access.column_policy_name"),
        cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
      },
      {
        accessorKey: "driver_group_id",
        header: t("operator.orgs.access.column_who"),
        cell: ({ getValue }) => <GroupRefCell name={driverGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "charger_group_id",
        header: t("operator.orgs.access.column_where"),
        cell: ({ getValue }) => <GroupRefCell name={chargerGroupMap.get(getValue() as number)} />,
      },
      {
        accessorKey: "enabled",
        header: t("operator.orgs.access.column_status"),
        cell: ({ row }) => (
          <Button size="xs"
            variant={row.original.enabled ? "default" : "outline"}
            onClick={(e) => { e.stopPropagation(); togglePolicyM.mutate({ id: row.original.id, enabled: !row.original.enabled }); }}
          >
            {t("operator.orgs.access.enabled_toggle", { state: row.original.enabled ? "on" : "off" })}
          </Button>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex gap-1">
            <Button size="xs" variant="outline"
              onClick={(e) => { e.stopPropagation(); setEditingPolicy(row.original); }}>
              {t("common.action.edit")}
            </Button>
            <Button size="icon-xs" variant="ghost" className="text-destructive hover:text-destructive"
              aria-label={t("common.action.delete")}
              onClick={(e) => {
                e.stopPropagation();
                if (confirm(t("operator.orgs.access.policy_delete_confirm", { name: row.original.name }))) deletePolicyM.mutate(row.original);
              }}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ),
      },
    ],
    [driverGroupMap, chargerGroupMap, chargerGroups, allPolicies, t, language, revision],
  );

  return (
    <div className="space-y-5 pt-[86px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t("operator.nav.access_control")}</h1>
          <div className="text-sm opacity-70">{t("operator.orgs.access.subtitle")}</div>
        </div>
        {showOrgSelector && (
          <OrgSelector orgs={orgs} selected={selectedOrg} onSelect={setSelectedOrg} />
        )}
      </div>

      {!selectedOrg ? (
        <Card>
          <CardContent className="py-12 text-center text-sm opacity-50">
            {t("operator.orgs.access.select_org")}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Groups side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="space-y-3">
              <h2 className="text-lg font-semibold">{t("operator.orgs.access.driver_groups_title")}</h2>
              <div className="flex gap-2">
                <Input placeholder={t("operator.orgs.access.new_group_placeholder")} value={newDriverName} onChange={(e) => setNewDriverName(e.target.value)} className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter" && newDriverName.trim() && !isReservedManualGroupName(newDriverName)) createDriverM.mutate(); }} />
                <Button onClick={() => { if (!isReservedManualGroupName(newDriverName)) createDriverM.mutate(); }} disabled={createDriverM.isPending || !newDriverName.trim() || isReservedManualGroupName(newDriverName)}>{t("common.action.create")}</Button>
              </div>
              {isReservedManualGroupName(newDriverName) && (
                <div className="text-xs text-destructive">{t(RESERVED_GROUP_NAME_MESSAGE_KEY)}</div>
              )}
              {driverGroupsQ.isLoading ? <div className="text-sm opacity-50">{t("common.state.loading")}</div>
                : visibleDriverGroups.length === 0 ? <div className="text-sm opacity-50 italic">{t("operator.orgs.access.no_driver_groups")}</div>
                : <div className="space-y-3">
                    {visibleDriverGroups.map((g) => (
                      <GroupCard key={g.id} group={g} type="driver" orgId={selectedOrg} policies={allPolicies}
                        onDeleted={refreshAll} userMap={userMap} />
                    ))}
                  </div>}
            </div>

            <div className="space-y-3">
              <h2 className="text-lg font-semibold">{t("operator.orgs.access.charger_groups_title")}</h2>
              <div className="flex gap-2">
                <Input placeholder={t("operator.orgs.access.new_group_placeholder")} value={newChargerName} onChange={(e) => setNewChargerName(e.target.value)} className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter" && newChargerName.trim() && !isReservedManualGroupName(newChargerName)) createChargerM.mutate(); }} />
                <Button onClick={() => { if (!isReservedManualGroupName(newChargerName)) createChargerM.mutate(); }} disabled={createChargerM.isPending || !newChargerName.trim() || isReservedManualGroupName(newChargerName)}>{t("common.action.create")}</Button>
              </div>
              {isReservedManualGroupName(newChargerName) && (
                <div className="text-xs text-destructive">{t(RESERVED_GROUP_NAME_MESSAGE_KEY)}</div>
              )}
              {chargerGroupsQ.isLoading ? <div className="text-sm opacity-50">{t("common.state.loading")}</div>
                : visibleChargerGroups.length === 0 ? <div className="text-sm opacity-50 italic">{t("operator.orgs.access.no_charger_groups")}</div>
                : <div className="space-y-3">
                    {visibleChargerGroups.map((g) => (
                      <GroupCard key={g.id} group={g} type="charger" orgId={selectedOrg} policies={allPolicies}
                        onDeleted={refreshAll} orgUnitIds={orgDetailQ.data?.organization?.unit_ids} />
                    ))}
                  </div>}
            </div>
          </div>

          {/* Access Rules (primary) */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t("operator.orgs.access.rules_title")}</CardTitle>
                  <div className="text-sm opacity-60 mt-1">
                    {t("operator.orgs.access.rules_help")}
                  </div>
                </div>
                <Button onClick={() => { setEditingRule(null); setRuleModalOpen(true); }}
                  disabled={driverGroups.length === 0}>
                  {t("operator.orgs.access.new_rule")}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {driverGroups.length === 0 ? (
                <div className="text-sm opacity-50 italic py-4 text-center">
                  {t("operator.orgs.access.rules_need_group")}
                </div>
              ) : (
                <DataTable columns={ruleColumns} data={rules} isLoading={rulesQ.isLoading}
                  getRowId={(r) => String(r.id)}
                  emptyMessage={t("operator.orgs.access.rules_empty")} />
              )}
            </CardContent>
          </Card>

          {/* Advanced Policies (collapsed, only if manual policies exist) */}
          {manualPolicies.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between cursor-pointer" onClick={() => setAdvancedOpen(!advancedOpen)}>
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {t("operator.orgs.access.advanced_title")}
                      <Badge variant="neutral" className="badge-sm">{manualPolicies.length}</Badge>
                    </CardTitle>
                    <div className="text-sm opacity-60 mt-1">
                      {t("operator.orgs.access.advanced_help")}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost">{t(advancedOpen ? "common.action.collapse" : "common.action.expand")}</Button>
                </div>
              </CardHeader>
              {advancedOpen && (
                <CardContent>
                  <div className="flex justify-end mb-3">
                    <Button size="sm" variant="outline" onClick={() => { setEditingPolicy(null); setPolicyModalOpen(true); }}>
                      {t("operator.orgs.access.new_advanced_policy")}
                    </Button>
                  </div>
                  <DataTable columns={policyColumns} data={manualPolicies} isLoading={manualPoliciesQ.isLoading}
                    getRowId={(p) => String(p.id)} />
                </CardContent>
              )}
            </Card>
          )}


          {/* Rule Create / Edit Modal */}
          <RuleFormModal
            open={ruleModalOpen || !!editingRule}
            onOpenChange={(v) => { if (!v) { setRuleModalOpen(false); setEditingRule(null); } }}
            orgId={selectedOrg}
            driverGroups={driverGroups}
            chargerGroups={chargerGroups}
            orgUsers={orgDetailQ.data?.organization?.users ?? []}
            onSaved={refreshAll}
            editRule={editingRule}
            orgUnitIds={orgDetailQ.data?.organization?.unit_ids}
          />

          {/* Legacy Policy Create / Edit Modal */}
          <PolicyFormModal
            open={policyModalOpen || !!editingPolicy}
            onOpenChange={(v) => { if (!v) { setPolicyModalOpen(false); setEditingPolicy(null); } }}
            orgId={selectedOrg}
            driverGroups={driverGroups}
            chargerGroups={chargerGroups}
            onSaved={refreshAll}
            editPolicy={editingPolicy}
            orgUnitIds={orgDetailQ.data?.organization?.unit_ids}
          />
        </>
      )}
    </div>
  );
}
